export type MfgLayoutProfile = 'supervisor' | 'manager' | 'viewer';

export interface MfgLayoutVisibility {
  myQueue: boolean;
  aiBrief: boolean;
  execution: boolean;
  fabrication: boolean;
  inspections: boolean;
  material: boolean;
  fulfilment: boolean;
  outsourced: boolean;
  delivery: boolean;
  traceDocs: boolean;
  routeSheets: boolean;
  documents: boolean;
  plant: boolean;
  productionRequests: boolean;
  bottlenecks: boolean;
  timeline: boolean;
  analytics: boolean;
  alerts: boolean;
}

export function getMfgControlTowerLayout(user: {
  is_super_admin?: boolean;
  permissions?: string[];
  preferences?: string | Record<string, unknown> | null;
  id?: string;
} | null): { profile: MfgLayoutProfile; visibility: MfgLayoutVisibility } {
  let override: MfgLayoutProfile | undefined;
  if (user?.preferences) {
    try {
      const prefs =
        typeof user.preferences === 'string' ? JSON.parse(user.preferences) : user.preferences;
      const raw = prefs?.mfg_control_tower_layout;
      if (raw === 'supervisor' || raw === 'manager' || raw === 'viewer') {
        override = raw;
      }
    } catch {
      /* ignore */
    }
  }

  const perms = user?.permissions ?? [];
  const isSuper = Boolean(user?.is_super_admin);
  const canManage = perms.includes('manufacturing.control_tower.manage') || isSuper;

  let profile: MfgLayoutProfile = override ?? (canManage ? 'manager' : 'supervisor');
  if (!override) {
    if (isSuper || canManage) profile = 'manager';
    else if (perms.includes('manufacturing.control_tower.view') || perms.includes('manufacturing.view')) {
      profile = 'supervisor';
    } else {
      profile = 'viewer';
    }
  }

  const allOn: MfgLayoutVisibility = {
    myQueue: true,
    aiBrief: true,
    execution: true,
    fabrication: true,
    inspections: true,
    material: true,
    fulfilment: true,
    outsourced: true,
    delivery: true,
    traceDocs: true,
    routeSheets: true,
    documents: true,
    plant: true,
    productionRequests: true,
    bottlenecks: true,
    timeline: true,
    analytics: true,
    alerts: true,
  };

  switch (profile) {
    case 'supervisor':
      return {
        profile,
        visibility: {
          ...allOn,
          routeSheets: false,
          documents: false,
          productionRequests: false,
          analytics: true,
          delivery: true,
          outsourced: false,
        },
      };
    case 'viewer':
      return {
        profile,
        visibility: {
          ...allOn,
          myQueue: false,
          aiBrief: true,
          fabrication: false,
          material: false,
        },
      };
    case 'manager':
    default:
      return {
        profile,
        visibility: {
          ...allOn,
          myQueue: false,
          fabrication: true,
          material: true,
        },
      };
  }
}

export function getMfgRefreshIntervalMs(
  user: { preferences?: string | Record<string, unknown> | null } | null
): number | false {
  if (!user?.preferences) return 30_000;
  try {
    const prefs =
      typeof user.preferences === 'string' ? JSON.parse(user.preferences) : user.preferences;
    const mins = Number(prefs?.mfg_tower_refresh_minutes);
    if (mins === 0) return false;
    if (mins === 1 || mins === 5 || mins === 15 || mins === 30) return mins * 60 * 1000;
  } catch {
    /* ignore */
  }
  return 30_000;
}
